import { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import buildingIcon from '../../assets/images/building-icon.png';
import { useHistory, useParams } from 'react-router-dom';
import RouteParams from '../../interfaces/general/Route';
import FilterMenuPanel from './components/FilterMenuPanel';
import TradeProbabilityInfo from './models/tradeProbabilityInfo';
import TradeProbabilityService from '../../services/TradeProbabilityService';
import IPropertyInfo from '../../components/shared/PropertiesMap/iPropertyInfo';
import PropertiesMap from '../../components/shared/PropertiesMap/PropertiesMap';
import MainLoader from '../../components/shared/MainLoader';

const MapFullHeight = 'calc(100vh - 60px)';
const MaxPriceFilter = 500000000;

const SearchingMap: FunctionComponent = ({ children }) => {
    const getItemPrice = (item: TradeProbabilityInfo): number | undefined => {
        const dynamicItem = item as any;
        const rawValue = dynamicItem.price ?? dynamicItem.p ?? dynamicItem.Price;
        const parsedValue = Number(rawValue);
        return isNaN(parsedValue) ? undefined : parsedValue;
    };

    const getItemPrediction = (item: TradeProbabilityInfo): number | undefined => {
        const dynamicItem = item as any;
        const rawValue = dynamicItem.allPredictions ?? dynamicItem.ap ?? dynamicItem.AllPredictions;
        const parsedValue = Number(rawValue);
        return isNaN(parsedValue) ? undefined : parsedValue;
    };

    const { t } = useTranslation(['f', 'b']),
        history = useHistory(),
        { id } = useParams<RouteParams>(),
        [isLoading, setIsLoading] = useState<boolean>(false),
        [selectedMenu, setSelectedMenu] = useState<number>(0),
        [mapHeight, setMapHeight] = useState<string>(MapFullHeight),
        [minPrice, setMinPrice] = useState<number>(0),
        [selectedPriceRange, setSelectedPriceRange] = useState<[number, number]>(),
        [selectedYears, setSelectedYears] = useState<number[]>([]),
        [selectedSectors, setSelectedSectors] = useState<number[]>([]),
        [sectorDataVersion, setSectorDataVersion] = useState<number>(0),
        [filterItems, setFilterItems] = useState<Array<TradeProbabilityInfo>>(),
        [tradeProbabilities, setTradeProbabilities] = useState<Array<TradeProbabilityInfo>>();
    const sectorIdByTradeIdRef = useRef<Map<string, number | null>>(new Map());

    const fetchTradeProbabilities = useCallback(async (region: string) => {
        try {
            setIsLoading(true);
            const tradeProbabilities = await TradeProbabilityService.fetchTradeProbabilities(region);

            setFilterItems(tradeProbabilities);
            setTradeProbabilities(tradeProbabilities);

            const prices = tradeProbabilities
                .map(x => getItemPrice(x))
                .filter((x): x is number => x !== undefined);
            setMinPrice(prices.length > 0 ? Math.min(...prices) : 0);
            setIsLoading(false);
        } catch (error: any) {
            toast(t(`b:${error.response.data.errorMessage}`));
        }
    }, []);

    useEffect(() => {
        setMapHeight(id ? 'calc(50vh - 30px)' : MapFullHeight);
    }, [id]);

    const handleBuildingClicked = (property: IPropertyInfo) => {
        history.push(`/trade-probability/${property.id}`);
    };

    const mapSectorToId = (sector: string): number | undefined => {
        const normalized = sector.trim().toLowerCase();
        switch (normalized) {
            case 'office':
                return 1;
            case 'residential':
                return 2;
            case 'alternatives':
                return 3;
            case 'retail':
                return 4;
            case 'hospitality':
                return 5;
            case 'land / development site':
                return 6;
            case 'industrial & logistics':
                return 7;
            case 'healthcare':
                return 8;
            default:
                return undefined;
        }
    };

    const getItemSectorId = (item: TradeProbabilityInfo): number | undefined => {
        const dynamicItem = item as any;
        const directSector = dynamicItem.sectorId ?? dynamicItem.s ?? dynamicItem.sector;
        if (typeof directSector === 'number') {
            return directSector;
        }
        if (typeof directSector === 'string') {
            const parsed = Number(directSector);
            return isNaN(parsed) ? mapSectorToId(directSector) : parsed;
        }
        if (typeof dynamicItem.mainType === 'string') {
            return mapSectorToId(dynamicItem.mainType);
        }
        return undefined;
    };

    const resolveMissingSectorData = useCallback(async (items: TradeProbabilityInfo[]) => {
        const missingItems = items.filter(item =>
            getItemSectorId(item) === undefined && !sectorIdByTradeIdRef.current.has(item.id)
        );

        if (missingItems.length === 0) {
            return;
        }

        try {
            setIsLoading(true);
            await Promise.all(missingItems.map(async item => {
                try {
                    const detail = await TradeProbabilityService.fetchTradeProbability(item.id);
                    const sectorId = mapSectorToId((detail as any)?.mainType || '');
                    sectorIdByTradeIdRef.current.set(item.id, sectorId ?? null);
                } catch {
                    sectorIdByTradeIdRef.current.set(item.id, null);
                }
            }));
        } finally {
            setIsLoading(false);
            setSectorDataVersion(v => v + 1);
        }
    }, []);

    const handlePriceChanged = ([minValue, maxValue]) => {
        setSelectedPriceRange([minValue, maxValue]);
    }

    const handleYearChanged = (values: number[]) => {
        setSelectedYears(values);
    };

    const handleSectorChanged = (values: number[]) => {
        setSelectedSectors(values);
    };

    const handleRegionChanged = (region: string) => {
        fetchTradeProbabilities(region);
    };

    useEffect(() => {
        if (!tradeProbabilities || selectedSectors.length === 0) {
            return;
        }

        const hasInlineSectorData = tradeProbabilities.some(x => getItemSectorId(x) !== undefined);
        if (!hasInlineSectorData) {
            resolveMissingSectorData(tradeProbabilities);
        }
    }, [tradeProbabilities, selectedSectors, resolveMissingSectorData]);

    useEffect(() => {
        if (!tradeProbabilities) {
            return;
        }

        let filtered = tradeProbabilities;
        if (selectedPriceRange) {
            const [minValue, maxValue] = selectedPriceRange;
            filtered = filtered.filter(x => {
                const price = getItemPrice(x);
                if (price === undefined) {
                    return true;
                }

                return price >= minValue && (maxValue === MaxPriceFilter || price <= maxValue);
            });
        }

        if (selectedYears.length > 0) {
            filtered = filtered.filter(x => {
                const prediction = getItemPrediction(x);
                if (prediction === undefined) {
                    return true;
                }

                return selectedYears.includes(prediction);
            });
        }

        if (selectedSectors.length > 0) {
            filtered = filtered.filter(x => {
                const sectorId = getItemSectorId(x) ?? sectorIdByTradeIdRef.current.get(x.id) ?? undefined;
                return sectorId ? selectedSectors.includes(sectorId) : false;
            });
        }

        setFilterItems(filtered);
    }, [tradeProbabilities, selectedPriceRange, selectedYears, selectedSectors, sectorDataVersion]);

    return (
        <div className="property-hero searching-map">
            <figure>
                <div className="background-cover">
                    <div className='navigation-icon'>
                        <img src={buildingIcon} onClick={() => setSelectedMenu(0)} />
                    </div>
                    <div className='search-content'>
                        <div className='search-panel'>
                            <div style={{ display: selectedMenu == 0 ? 'block' : 'none' }}>
                                <FilterMenuPanel minPrice={minPrice} maxPrice={MaxPriceFilter} tradeProbabilities={tradeProbabilities} onSectorChanged={handleSectorChanged} onPriceChanged={handlePriceChanged} onYearChanged={handleYearChanged} onRegionChanged={handleRegionChanged} />
                            </div>
                        </div>
                        <div className="map-panel">
                            <PropertiesMap type={TradeProbabilityInfo} height={mapHeight} markerId={id} properties={filterItems} onMarkerClicked={handleBuildingClicked} />
                            {children}
                            {!isLoading ? null : <div className='loading'>
                                <MainLoader type="regular" size={100} />
                            </div>}
                        </div>
                    </div>
                </div>
            </figure>
        </div>
    );
};

export default SearchingMap;
